import { useNavigate } from "react-router-dom";
import { faLock, faMagnifyingGlass, faPencil } from "@fortawesome/free-solid-svg-icons";
import { useRef, useState } from "react";
import { GridColumn } from "@progress/kendo-react-grid/dist/npm/GridColumn";
import { useTheme } from "@mui/material";
import {
  GridDataStateChangeEvent,
  GridRowDoubleClickEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { DataResult, State } from "@progress/kendo-data-query";
import { FtlRouteGridColumns } from "./FtlRouteGridColumns";
import { FtlRoute } from "./FtlRoute";
import { ftlRouteUrls } from "../../URL/ApplicationUrls";
import PageHeader from "../../Layout/Header/PageHeader";
import { combinedUrls } from "../../URL/functions";
import { ftlRouteOverviewToolbarMenu } from "./FtlRouteOverviewToolbarMenu";
import { GRID_SELECTED_FIELD } from "../../Grid/Selectable/GridSelection";
import { isColumnFilterActive } from "../../Grid/Extensions/FilterStatusHelper";
import { GridDataLoader } from "../../Grid/GridDataLoader";
import StyledKendoGrid from "../../Grid/StyledKendoGrid";
import { InitialState } from "../../Grid/InitialDataState";
import { GridSelectableRowsProps } from "../../Grid/GridSelectableRowsProps";
import useSavedState from "../../Grid/UseSavedState";
import StatePersistenceButtons from "../../Grid/StatePersistenceButtons";
import { UppercaseButton } from "../../Layout/Buttons/UppercaseButton";
import { IconWithRightMargin } from "../../Layout/Icons/IconWithRightMargin";
import { KENDO_BUTTON_CLASSES } from "../../Layout/Buttons/KendoButtons";
import { isArrayUndefinedOrEmpty } from "../../Utils/arrays";
import { useFtlRoutesOverviewActions } from "./useFtlRoutesOverviewActions";
import { gridPagerDefaultSettings } from "../../Grid/GridConstants";
import { getFtlRouteIds } from "../../Grid/gridUtils";
import { AuthorizationPolicies } from "../../Auth/AuthorizationPolicy";
import RenderAuthorized from "../../Auth/RenderAuthorized";

const FtlRouteOverviewGridLocalStorageKey = "FtlRouteOverviewGrid_DataState";

export default function FtlRouteOverview() {
  const navigate = useNavigate();

  // Overview Actions
  const { showDetails, createProject, createTtProtection } = useFtlRoutesOverviewActions();

  const [dataResult, setDataResult] = useState<DataResult>({
    data: [],
    total: 0,
  });

  const initialDataState: State = {
    ...InitialState,
  };

  const [dataState, setDataState] = useSavedState<State>(
    FtlRouteOverviewGridLocalStorageKey,
    initialDataState
  );

  // Row selection
  const [selectedState, setSelectedState] = useState<{
    [id: number]: boolean | number[];
  }>({});
  const [selectedIds, setSelectedIds] = useState<Array<number>>([]);
  const [allItemsSelected, setAllItemsSelected] = useState<boolean>(false);
  const selectableRowsProps = GridSelectableRowsProps<FtlRoute>({
    selectedState,
    setSelectedState,
    setSelectedIds,
    dataItemKey: "RouteId",
    setAllItemsSelected,
  });

  const dataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };

  const handleRowDoubleClick = (e: GridRowDoubleClickEvent) => {
    const ftlRoute = e.dataItem as FtlRoute;
    navigate(ftlRouteUrls.detailsFor(ftlRoute.RouteId));
  };

  const onDataReceived = (ftlRoutes: DataResult) => {
    setDataResult(ftlRoutes);
  };

  const GridRef = useRef<any>();
  const url = combinedUrls(ftlRouteUrls.base.apiUrl, "KendoGrid");
  const theme = useTheme();

  const columns = FtlRouteGridColumns({ dataResult, selectedState });

  const ftlRouteExportMenu = ftlRouteOverviewToolbarMenu(url);
  const exportMenu = ftlRouteExportMenu({
    dataState,
    ref: GridRef,
    selectedIds,
    tableDataResult: dataResult,
    allItemsSelected,
  });

  return (
    <>
      <PageHeader headerText="FTL Routes" />
      <StyledKendoGrid
        muiTheme={theme}
        ref={GridRef}
        height="65vh"
        sortable
        filterable
        pageable={{
          ...gridPagerDefaultSettings,
        }}
        resizable
        scrollable="scrollable"
        {...dataState}
        data={{
          data: dataResult.data.map((item) => ({
            ...item,
            [GRID_SELECTED_FIELD]: selectedState[item.RouteId],
          })),
          total: dataResult.total,
        }}
        onDataStateChange={dataStateChange}
        onRowDoubleClick={handleRowDoubleClick}
        rowHeight={50}
        {...selectableRowsProps}
      >
        <GridToolbar>
          <StatePersistenceButtons
            localStorageKey={FtlRouteOverviewGridLocalStorageKey}
            initialState={initialDataState}
            dataState={dataState}
            resetButtonText="RESET FILTER"
            saveButtonText="SAVE FILTER"
            align="left"
          />
          <UppercaseButton
            type="button"
            title="VIEW DETAILS"
            className={KENDO_BUTTON_CLASSES}
            onClick={() => showDetails(selectedIds[0])}
            disabled={selectedIds.length !== 1}
          >
            <IconWithRightMargin icon={faMagnifyingGlass} />
            VIEW DETAILS
          </UppercaseButton>
          <RenderAuthorized requiredRoles={AuthorizationPolicies.RequireAdminNetworkRoles}>
            <UppercaseButton
              type="button"
              title="CREATE PROJECT"
              className={KENDO_BUTTON_CLASSES}
              onClick={async () => {
                const routeIds: number[] = await getFtlRouteIds({
                  selectedIds,
                  currentDatastate: dataState,
                  tableDataResult: dataResult,
                  allItemsSelected,
                });
                if (isArrayUndefinedOrEmpty(routeIds)) return;
                await createProject(routeIds);
              }}
              disabled={isArrayUndefinedOrEmpty(selectedIds)}
            >
              <IconWithRightMargin icon={faPencil} />
              CREATE PROJECT
            </UppercaseButton>
          </RenderAuthorized>
          <RenderAuthorized requiredRoles={AuthorizationPolicies.RequireAdminRiskmanagerRoles}>
            <UppercaseButton
              type="button"
              title="GENERATE TT PROTECTION"
              className={KENDO_BUTTON_CLASSES}
              onClick={async () => {
                const routeIds: number[] = await getFtlRouteIds({
                  selectedIds,
                  currentDatastate: dataState,
                  tableDataResult: dataResult,
                  allItemsSelected,
                });
                if (isArrayUndefinedOrEmpty(routeIds)) return;
                await createTtProtection(routeIds);
              }}
              disabled={isArrayUndefinedOrEmpty(selectedIds)}
            >
              <IconWithRightMargin icon={faLock} />
              GENERATE TT PROTECTION
            </UppercaseButton>
          </RenderAuthorized>
          <RenderAuthorized
            requiredRoles={AuthorizationPolicies.RequireAdminReadOnlyNetworkRiskLaunch}
          >
            {exportMenu}
          </RenderAuthorized>
        </GridToolbar>
        {columns.map((column) => (
          <GridColumn
            key={column.key}
            {...column.props}
            headerClassName={
              isColumnFilterActive(column.props.field, dataState) ? "filterActive" : ""
            }
          />
        ))}
      </StyledKendoGrid>
      <GridDataLoader url={url} dataState={dataState} onDataReceived={onDataReceived} />
    </>
  );
}
